import React, { useState } from "react";
import Message from "@dashboardAdmin/Message";
import { DownloadIcon, EyeIcon } from "@assets/svgIcons";
import { truncateText } from "@src/utils";
import Pagination from "../Pagination";
import ViewDetailsModal from "./components/ViewDetailsModal";
import { COLUMNS_TO_SHOW } from "../../homePageData";
import "./styles.scss";

const FormSubmissionsTable = ({
  visibleData,
  paginatedData,
  loading,
  fetchError,
  debugInfo,
  tableData,
  minSubmittedDate,
  maxSubmittedDate,
  filters,
  search,
  onSearchChange,
  onFilterChange,
  onDownloadFiltered,
  onDownloadAll,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  return (
    <div>
      <div className="filter-row">
        <span className="filter-label">Search:</span>

        <input
          type="text"
          placeholder="Search all data"
          className="filter-input"
          value={search}
          onChange={onSearchChange}
          aria-label="Search all table data"
        />

        <span className="filter-label">Consent:</span>

        <select
          className="filter-input"
          value={filters.consent}
          onChange={(e) => onFilterChange("consent", e.target.value)}
          aria-label={`Filter by consent`}
        >
          <option value="">All</option>
          <option value="true">Consent Yes</option>
          <option value="false">Consent No</option>
        </select>

        <span className="filter-label">Date Range:</span>

        <div className="date-input-wrapper">
          <input
            type="date"
            className="filter-input "
            min={minSubmittedDate || undefined}
            max={filters.submitted_to || maxSubmittedDate || undefined}
            value={filters.submitted_from || ""}
            onChange={(e) => onFilterChange("submitted_from", e.target.value)}
            aria-label="Submitted from date"
          />
          {filters.submitted_from && (
            <button
              className="clear-date-btn"
              onClick={() => onFilterChange("submitted_from", "")}
              aria-label="Clear from date"
              title="Clear date"
            >
              ×
            </button>
          )}
        </div>

        <span style={{ fontWeight: 500, color: "#18214b" }}>to</span>

        <div className="date-input-wrapper">
          <input
            type="date"
            className="filter-input "
            min={filters.submitted_from || minSubmittedDate || undefined}
            max={maxSubmittedDate || undefined}
            value={filters.submitted_to || ""}
            onChange={(e) => onFilterChange("submitted_to", e.target.value)}
            aria-label="Submitted to date"
          />
          {filters.submitted_to && (
            <button
              className="clear-date-btn"
              onClick={() => onFilterChange("submitted_to", "")}
              aria-label="Clear to date"
              title="Clear date"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="download-buttons">
        <button
          className="download-btn"
          disabled={loading || !visibleData.length}
          onClick={onDownloadFiltered}
          aria-label="Download filtered data as CSV"
        >
          <DownloadIcon />
          Download Filtered CSV
        </button>

        <button
          className="download-btn"
          style={{
            background:
              loading || !tableData.length
                ? "#b2c2e0"
                : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          }}
          onClick={onDownloadAll}
          aria-label="Download all data as CSV"
          disabled={loading || !tableData.length}
        >
          <DownloadIcon />
          Download All CSV
        </button>
      </div>

      <div className="table-wrapper admin-scroll-hide-x">
        {loading && <div className="status-msg">Loading...</div>}
        {fetchError && (
          <Message type="error" showIcon>
            <strong>Error:</strong> {fetchError}
            {debugInfo?.query && (
              <div style={{ fontSize: "0.92em", color: "#555", marginTop: 6 }}>
                <div>Debug Info:</div>
                <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            )}
          </Message>
        )}

        {!loading && !fetchError && (
          <>
            {debugInfo && debugInfo.query && (
              <div
                style={{ fontSize: "0.95em", color: "#888", marginBottom: 8 }}
              >
                {typeof debugInfo.count === "number" && (
                  <span style={{ marginLeft: 16 }}>
                    Rows: {tableData.length}
                  </span>
                )}
              </div>
            )}

            <table className="admin-table">
              <thead>
                <tr>
                  {COLUMNS_TO_SHOW.map((col) => (
                    <th className="table-header" key={col.key}>
                      {col.label}
                    </th>
                  ))}
                  <th className="table-header actions-column">View</th>
                </tr>
              </thead>
              <tbody>
                {visibleData.length === 0 ? (
                  <tr>
                    <td
                      className="table-cell"
                      colSpan={COLUMNS_TO_SHOW.length + 1}
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, idx) => (
                    <tr
                      key={row.id ?? idx}
                      style={{
                        background: idx % 2 === 1 ? "#f8faff" : "#fff",
                        transition: "background 0.15s",
                      }}
                    >
                      {COLUMNS_TO_SHOW.map((col) => {
                        const cellValue = String(row[col.key] ?? "");
                        const displayValue = truncateText(cellValue);
                        const isTruncated = cellValue.length > 25;

                        return (
                          <td
                            className="table-cell"
                            key={col.key}
                            title={isTruncated ? cellValue : ""}
                          >
                            {displayValue}
                          </td>
                        );
                      })}

                      <td className="table-cell actions-cell">
                        <button
                          className="view-details-btn"
                          onClick={() => handleViewDetails(row)}
                          aria-label="View details"
                          title="View details"
                        >
                          <EyeIcon />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </>
        )}
      </div>

      {/* View Details Modal */}
      <ViewDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedRow}
      />
    </div>
  );
};

export default FormSubmissionsTable;
